import { useBetaFeature } from 'store/beta-features';
import { isInIframe } from 'utils/isInIframe';
import { useSelector } from 'react-redux';
import { getClasses } from 'common.components/user/selectors';
import { getGetPattern } from './getGetPattern';

interface Props {
    experimentsFlags: Partial<Record<string, number>> | null;
    featureFlags: Partial<Record<string, boolean>> | null;
    teamId: number | string | undefined;
    subject: string | undefined;
    isInformaticsTeacher: boolean;
    staticUrls: Record<string, string>;
    userId?: number;
}

/**
 * Позволяет узнать есть ли у пользователя доступ к определенному набору страниц.
 *
 * Так же для каждого набора возвращает соответствующий урл для редиректа в старый учебник.
 */
// eslint-disable-next-line complexity
export const useNewLabPathAccess = ({
    experimentsFlags,
    teamId,
    subject,
    isInformaticsTeacher,
    staticUrls,
    featureFlags,
}: Props) => {
    const getPattern = getGetPattern({ teamId, subject });
    const teams = useSelector(getClasses);

    const commonBetaAccess = useBetaFeature('teacherMultiAssignmentEnabled');
    const isNewSchoolbookAvailable = teams.length > 0;
    const isInformaticsSelected = !subject || subject === 'informatics';

    const isPublicViewAvailable = teams
        ? experimentsFlags?.enableNewPublicShowcase === 1 && teams.length === 0
        : experimentsFlags?.enableNewPublicShowcase === 1;

    const newLabAccess = {
        materials:
            isPublicViewAvailable ||
            (!isInIframe() &&
                isNewSchoolbookAvailable &&
                experimentsFlags?.enableNewMaterials === 1 &&
                isInformaticsSelected &&
                isInformaticsTeacher &&
                commonBetaAccess.isActive),
        // журнал открывается в iframe из нового сервиса
        // teamId чтобы не зацикливались редиректы из старого в новый при отсутствии классов
        journal: isPublicViewAvailable || (isNewSchoolbookAvailable && !isInIframe() && teamId),
        hrReserve: isPublicViewAvailable || (isNewSchoolbookAvailable && !featureFlags?.teacherReserveInactive),
        KPKs: isPublicViewAvailable || (isNewSchoolbookAvailable && !featureFlags?.teacherReserveInactive),
        portfolio: isNewSchoolbookAvailable && experimentsFlags?.enableNewPortfolio === 1,
        lessons:
            isPublicViewAvailable ||
            (!isInIframe() &&
                isNewSchoolbookAvailable &&
                experimentsFlags?.enableNewLessons === 1 &&
                commonBetaAccess.isActive),
        specProjects:
            (isNewSchoolbookAvailable || experimentsFlags?.enableNewPublicShowcase) &&
            !isInIframe() &&
            experimentsFlags?.enableNewSpecCourses === 1,
        teams:
            isPublicViewAvailable ||
            (!isInIframe() &&
                isNewSchoolbookAvailable &&
                experimentsFlags?.enableNewClasses === 1 &&
                commonBetaAccess.isActive),
        profile: isNewSchoolbookAvailable && experimentsFlags?.enableNewProfile === 1 && commonBetaAccess.isActive,
        publicShowcase: isPublicViewAvailable,
        showcase: experimentsFlags?.enableNewShowcase === 1 && commonBetaAccess.isActive && isInformaticsTeacher,
        liveEvents: experimentsFlags?.enableLiveEvents === 1,
    };

    const newLabPath = {
        materials: getPattern({
            noParams: staticUrls.newLabMaterialsCourseContainers,
            withTeamIdAndSubject: staticUrls.newLabTeamsSubjectMaterialsCourseContainers,
        }),
        container: getPattern({
            noParams: staticUrls.newLabMaterialsCourseContainer,
            withTeamIdAndSubject: staticUrls.newLabTeamsSubjectMaterialsCourseContainer,
        }),
        themes: getPattern({
            noParams: staticUrls.newLabMaterialsThemes,
            withTeamIdAndSubject: staticUrls.newLabTeamsSubjectMaterialsThemeId,
        }),
        journal: getPattern({
            noParams: staticUrls.newLabJournal,
            withTeamId: staticUrls.newLabTeamsJournal,
            withTeamIdAndSubject: staticUrls.newLabTeamsSubjectJournal,
        }),
        hrReserve: getPattern({
            noParams: staticUrls.newLabProfessionalGrowthActivities,
            withTeamId: staticUrls.newLabTeamsProfessionalGrowthActivities,
        }),
        KPKs: staticUrls.newLabProfessionalGrowthKPKs,
        portfolio: staticUrls.newLabProfessionalGrowthPortfolio,
        lessons: getPattern({
            noParams: staticUrls.newLabLessons,
            withTeamIdAndSubject: staticUrls.newLabTeamsLessons,
        }),
        specProjects: getPattern({
            noParams: staticUrls.newLabSpecProjects,
            withTeamId: staticUrls.newLabTeamsSpecProjects,
        }),
        specProject: getPattern({
            noParams: staticUrls.newLabSpecProjects,
            withTeamId: staticUrls.newLabTeamsSpecProject,
        }),
        teams: getPattern({
            noParams: staticUrls.newLabTeams,
            withTeamId: staticUrls.newLabTeam,
        }),
        profile: getPattern({
            noParams: staticUrls.newLabPersonalAccountMain,
            withTeamId: staticUrls.newLabPersonalAccountTeamMain,
        }),
        publicShowcase: getPattern({
            noParams: staticUrls.newLabLibraryMain,
        }),
        showcase: getPattern({
            noParams: staticUrls.newLabLibraryMain,
            withTeamId: staticUrls.newLabLibraryTeamMain,
        }),
        liveEvents: getPattern({
            noParams: staticUrls.newLabLiveEvents,
            withTeamId: staticUrls.newLabTeamsLiveEvents,
        }),
    };

    return { newLabAccess, newLabPath };
};
